import { useState, useCallback } from 'react';
import { message } from 'antd';
import { l205Api, FL150, FL151 } from '../../../services/l205Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useL205Mode';

export function useL205Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FL150 | null>(null);
  const [formData, setFormData] = useState<Partial<FL150>>({});
  const [details, setDetails] = useState<Partial<FL151>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FL150;
    dets: Partial<FL151>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await l205Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FL150, value: FL150[keyof FL150]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FL150): FL150[keyof FL150] => {
    return (formData as Record<string, FL150[keyof FL150]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FL151): FL151[keyof FL151] => {
    const sel = details[selectedRow] as Partial<FL151> | undefined;
    return sel ? (sel as Record<string, FL151[keyof FL151]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FL151, value: FL151[keyof FL151]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Update a specific detail row by index */
  const updateDetailRow = useCallback((idx: number, key: keyof FL151, value: FL151[keyof FL151]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FL150) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FL150,
        details: details as FL151[],
      };
      if (mode === 'add') {
        const result = await l205Api.create(req);
        message.success(`新增成功: ${result.fl150_01}`);
        onSuccess(result);
      } else {
        const result = await l205Api.update(formData.fl150_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await l205Api.delete(record.fl150_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fl151_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('最多 99 個明細項');
      return;
    }
    setDetails(prev => [...prev, {
      fl151_02: maxSeq + 1,
      fl151_01: formData.fl150_01 || '',
      fl151_03: '',
      fl151_03a: '',
      fl151_04: '',
      fl151_04a: '',
      fl151_11: '',
      fl151_12: '',
      fl151_21: '',
      fl151_22: '',
    }]);
    setField('fl150_15', (formData.fl150_15 || 0) + 1);
  }, [details, formData.fl150_01, formData.fl150_15, setField]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 1) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fl151_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
    setField('fl150_15', Math.max(0, (formData.fl150_15 || 0) - 1));
  }, [details.length, selectedRow, formData.fl150_15, setField]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add — 3 initial detail rows per spec */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    const now = new Date();
    const month = now.getMonth() + 1; // 1-12
    const period = month <= 6 ? 1 : 2;
    const isoDate = now.toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({
      fl150_10: period,
      fl150_11: isoDate,
      fl150_14: user?.userId || '',
      fl150_15: 3,
    });
    setDetails([
      { fl151_02: 1, fl151_03: '', fl151_11: '', fl151_12: '', fl151_21: '', fl151_22: '' },
      { fl151_02: 2, fl151_03: '', fl151_11: '', fl151_12: '', fl151_21: '', fl151_22: '' },
      { fl151_02: 3, fl151_03: '', fl151_11: '', fl151_12: '', fl151_21: '', fl151_22: '' },
    ]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FL150) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField, updateDetailRow,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors, hasErrors,
  };
}
